import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Avatar, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Divider, useTheme, alpha,
  Backdrop, CircularProgress, Snackbar,
  InputAdornment, Pagination, Badge,
  Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Card, CardContent
} from '@mui/material';
import {
  History as HistoryIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  DateRange as DateRangeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Visibility as VisibilityIcon,
  CalendarToday as CalendarIcon,
  ShoppingBasket as BasketIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types - Updated to handle both lowercase and uppercase status
interface DispenseOrder {
  order_id: string;
  receptionist_id: string;
  receptionist_name?: string;
  active_status: string; // Can be 'pending', 'completed', 'cancelled' or 'PENDING', 'COMPLETED', 'CANCELLED'
  transaction_category: string;
  notes?: string;
  total_amount?: number;
  created_at: string;
  updated_at?: string;
  total_items: number;
  total_quantity: number;
  housekeeper_name?: string;
}

interface DispenseItem {
  id: number;
  order_id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price?: number;
  created_at?: string;
}

interface OrderDetail {
  order: DispenseOrder;
  items: DispenseItem[];
}

interface FilterOptions {
  startDate: Date | null;
  endDate: Date | null;
  housekeeperId: string;
  status: 'all' | 'pending' | 'completed' | 'cancelled';
  search: string;
}

interface Housekeeper {
  user_id: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  fullName?: string;
}

const DispenseHistoryFb: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  
  // Data states
  const [orders, setOrders] = useState<DispenseOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<DispenseOrder[]>([]);
  const [housekeepers, setHousekeepers] = useState<Housekeeper[]>([]);
  
  // Detail view states
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState<boolean>(false);
  
  // Filter states
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)), // Last 30 days
    endDate: new Date(),
    housekeeperId: 'all',
    status: 'all',
    search: ''
  });
  
  // UI states
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Helper function to safely parse dates
  const safeParseDate = (dateString: string | undefined): Date => {
    if (!dateString) return new Date();
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString);
      return new Date();
    }
    return date;
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  };

  // Fetch housekeepers for filter dropdown
  const fetchHousekeepers = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        `${baseUrl()}Housekeepers/getHousekeepers`,
        formData,
        config
      );

      console.log("Housekeepers response:", response.data);

      if (response.data.status) {
        const housekeepersData = response.data.data || [];
        const formatted = housekeepersData.map((hk: any) => ({
          ...hk,
          fullName: `${hk.firstName || ''} ${hk.middleName ? hk.middleName + ' ' : ''}${hk.lastName || ''}`.trim()
        }));
        setHousekeepers(formatted);
      }
    } catch (error) {
      console.error("Error fetching housekeepers:", error);
    }
  };

  // Fetch dispense history
  const fetchDispenseHistory = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      if (filters.startDate) {
        formData.append("startDate", formatDate(filters.startDate));
      }
      if (filters.endDate) {
        formData.append("endDate", formatDate(filters.endDate));
      }
      if (filters.housekeeperId !== 'all') {
        formData.append("housekeeper_id", filters.housekeeperId);
      }
      if (filters.status !== 'all') {
        // Send uppercase to match API expected format
        formData.append("status", filters.status.toUpperCase());
      }

      const response = await axios.post(
        `${baseUrl()}BeverageSupervisors/getDispenseHistory`,
        formData,
        config
      );

      console.log("Dispense history response:", response.data);

      if (response.data.status) {
        const ordersData = response.data.data || [];
        setOrders(ordersData);
        applyFilters(ordersData);
      } else {
        showNotification('Failed to fetch dispense history', 'error');
      }
    } catch (error) {
      console.error("Fetch dispense history error:", error);
      showNotification('Error fetching dispense history', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch order details
  const fetchOrderDetails = async (orderId: string) => {
    setDetailsLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("order_id", orderId);

      const response = await axios.post(
        `${baseUrl()}BeverageSupervisors/getDispenseDetails`,
        formData,
        config
      );

      console.log("Order details response:", response.data);

      if (response.data.status) {
        setSelectedOrder({
          order: response.data.order,
          items: response.data.items || []
        });
        setDetailsModalOpen(true);
      } else {
        showNotification('Failed to fetch order details', 'error');
      }
    } catch (error) {
      console.error("Fetch order details error:", error);
      showNotification('Error fetching order details', 'error');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Apply filters
  const applyFilters = (data: DispenseOrder[]) => {
    let filtered = [...data];

    // Filter by search (order ID or housekeeper name)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(order => 
        order.order_id?.toLowerCase().includes(searchLower) ||
        order.housekeeper_name?.toLowerCase().includes(searchLower) ||
        order.receptionist_name?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredOrders(filtered);
    setPage(1);
  };

  // Handle filter changes
  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply filters when search changes
  useEffect(() => {
    applyFilters(orders);
  }, [filters.search, orders]);

  // Fetch data when date range or other filters change
  useEffect(() => {
    if (housekeepers.length > 0) {
      fetchDispenseHistory();
    }
  }, [filters.startDate, filters.endDate, filters.housekeeperId, filters.status]);

  // Initial load
  useEffect(() => {
    fetchHousekeepers();
  }, []);

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchDispenseHistory().finally(() => setRefreshing(false));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      endDate: new Date(),
      housekeeperId: 'all',
      status: 'all',
      search: ''
    });
  };

  // Handle close modal
  const handleCloseModal = () => {
    setDetailsModalOpen(false);
    setSelectedOrder(null);
  };

  // Get status icon - case insensitive
  const getStatusIcon = (status: string) => {
    const statusLower = status?.toLowerCase() || '';
    
    switch (statusLower) {
      case 'completed':
        return <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />;
      case 'cancelled':
        return <CancelIcon sx={{ color: '#ef4444', fontSize: 20 }} />;
      case 'pending':
        return <PendingIcon sx={{ color: '#f59e0b', fontSize: 20 }} />;
      default:
        return <PendingIcon sx={{ color: '#6b7280', fontSize: 20 }} />;
    }
  };

  // Get status chip - case insensitive
  const getStatusChip = (status: string) => {
    const statusLower = status?.toLowerCase() || 'pending';
    
    const colors: Record<string, { bg: string; color: string }> = {
      completed: { bg: '#10b981', color: 'white' },
      cancelled: { bg: '#ef4444', color: 'white' },
      pending: { bg: '#f59e0b', color: 'white' }
    };
    
    const color = colors[statusLower] || colors.pending;
    
    return (
      <Chip
        size="small"
        label={status} // Display original case from API
        sx={{
          bgcolor: alpha(color.bg, 0.1),
          color: color.bg,
          textTransform: 'capitalize',
          fontWeight: 600
        }}
      />
    );
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all' && v !== '').length;

  // Pagination
  const pageCount = Math.ceil(filteredOrders.length / rowsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const getDateRangeLabel = () => {
    if (filters.startDate && filters.endDate) {
      return `${formatDate(filters.startDate)} to ${formatDate(filters.endDate)}`;
    }
    return 'Select date range';
  };

  const getHousekeeperName = (order: DispenseOrder): string => {
    if (order.housekeeper_name) return order.housekeeper_name;
    if (order.receptionist_name) return order.receptionist_name;
    
    const housekeeper = housekeepers.find(h => h.user_id === order.receptionist_id);
    return housekeeper?.fullName || 'Unknown';
  };

  // Calculate statistics - case insensitive for status
  const getStats = () => {
    const totalOrders = filteredOrders.length;
    
    // Ensure we're working with numbers by parsing
    const totalItems = filteredOrders.reduce((sum, order) => {
      const items = typeof order.total_items === 'string' 
        ? parseInt(order.total_items, 10) 
        : (order.total_items || 0);
      return sum + items;
    }, 0);
    
    const totalQuantity = filteredOrders.reduce((sum, order) => {
      const qty = typeof order.total_quantity === 'string' 
        ? parseInt(order.total_quantity, 10) 
        : (order.total_quantity || 0);
      return sum + qty;
    }, 0);
    
    // Case-insensitive status filtering
    const pending = filteredOrders.filter(o => 
      o.active_status?.toLowerCase() === 'pending'
    ).length;
    
    const completed = filteredOrders.filter(o => 
      o.active_status?.toLowerCase() === 'completed'
    ).length;
    
    const cancelled = filteredOrders.filter(o => 
      o.active_status?.toLowerCase() === 'cancelled'
    ).length;

    return { totalOrders, totalItems, totalQuantity, pending, completed, cancelled };
  };

  const stats = getStats();

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
            
            {/* Header */}
            <Paper 
              elevation={0}
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper'
              }}
            >
              <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                  <IconButton onClick={() => history.goBack()}>
                    <BackIcon />
                  </IconButton>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.main
                    }}
                  >
                    <HistoryIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h5" fontWeight={600}>
                      Dispense History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Track all stock dispensed to housekeepers
                    </Typography>
                  </Box>
                  <Chip 
                    label={getDateRangeLabel()}
                    icon={<DateRangeIcon />}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Tooltip title="Refresh">
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={handleRefresh}
                      disabled={refreshing}
                      size="small"
                    >
                      Refresh
                    </Button>
                  </Tooltip>
                </Stack>
              </Container>
            </Paper>

            <Container maxWidth="xl" sx={{ py: 3 }}>
              
              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="body2">Total Orders</Typography>
                      <Typography variant="h4">{stats.totalOrders}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="body2">Total Items</Typography>
                      <Typography variant="h4">{stats.totalItems}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="body2">Total Quantity</Typography>
                      <Typography variant="h4">{stats.totalQuantity}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: alpha('#f59e0b', 0.05) }}>
                    <CardContent>
                      <Typography color="text.secondary" variant="body2">Pending</Typography>
                      <Typography variant="h4" color="#f59e0b">{stats.pending}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: alpha('#10b981', 0.05) }}>
                    <CardContent>
                      <Typography color="text.secondary" variant="body2">Completed</Typography>
                      <Typography variant="h4" color="#10b981">{stats.completed}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Filters */}
              <Paper sx={{ mb: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Badge badgeContent={activeFilterCount} color="primary">
                        <FilterIcon color="action" />
                      </Badge>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Filters
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </Stack>
                </Box>

                {showFilters && (
                  <>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="Start Date"
                            value={filters.startDate}
                            onChange={(date) => handleFilterChange('startDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="End Date"
                            value={filters.endDate}
                            onChange={(date) => handleFilterChange('endDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Housekeeper</InputLabel>
                            <Select
                              value={filters.housekeeperId}
                              label="Housekeeper"
                              onChange={(e) => handleFilterChange('housekeeperId', e.target.value)}
                            >
                              <MenuItem value="all">All Housekeepers</MenuItem>
                              {housekeepers.map(hk => (
                                <MenuItem key={hk.user_id} value={hk.user_id}>
                                  {hk.fullName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={filters.status}
                              label="Status"
                              onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                              <MenuItem value="all">All Statuses</MenuItem>
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="completed">Completed</MenuItem>
                              <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Search Order ID"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                              endAdornment: filters.search && (
                                <InputAdornment position="end">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleFilterChange('search', '')}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Showing {filteredOrders.length} orders
                        </Typography>
                        <Button
                          size="small"
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                        >
                          Clear All
                        </Button>
                      </Stack>
                    </Box>
                  </>
                )}
              </Paper>

              {/* Orders Table */}
              <Paper>
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date & Time</TableCell>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Housekeeper</TableCell>
                        <TableCell align="center">Items</TableCell>
                        <TableCell align="center">Total Qty</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : paginatedOrders.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                            <Stack spacing={2} alignItems="center">
                              <BasketIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                              <Typography color="text.secondary">No dispense records found</Typography>
                              <Button variant="outlined" size="small" onClick={clearFilters}>
                                Clear Filters
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedOrders.map((order) => (
                          <TableRow key={order.order_id} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {format(safeParseDate(order.created_at), 'MMM dd, yyyy HH:mm')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {order.order_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                                  {getHousekeeperName(order).charAt(0)}
                                </Avatar>
                                <Typography variant="body2">
                                  {getHousekeeperName(order)}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={order.total_items || 0}
                                size="small"
                                sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={order.total_quantity || 0}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              {getStatusChip(order.active_status)}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => fetchOrderDetails(order.order_id)}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {filteredOrders.length > 0 && (
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={(e, value) => setPage(value)}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </Paper>
            </Container>

            {/* Order Details Modal */}
            <Dialog
              open={detailsModalOpen}
              onClose={handleCloseModal}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <BasketIcon color="primary" />
                  <Typography variant="h6">
                    Order Details: {selectedOrder?.order?.order_id}
                  </Typography>
                </Stack>
                <IconButton
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                  onClick={handleCloseModal}
                >
                  <CancelIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {detailsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : selectedOrder ? (
                  <Stack spacing={3}>
                    {/* Order Summary */}
                    <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Order ID</Typography>
                          <Typography variant="body1" fontWeight={600}>{selectedOrder.order.order_id}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Housekeeper</Typography>
                          <Typography variant="body1">{getHousekeeperName(selectedOrder.order)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Date</Typography>
                          <Typography variant="body1">
                            {format(safeParseDate(selectedOrder.order.created_at), 'MMM dd, yyyy HH:mm')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">Status</Typography>
                          <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedOrder.order.active_status)}</Box>
                        </Grid>
                        {selectedOrder.order.notes && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">Notes</Typography>
                            <Typography variant="body1">{selectedOrder.order.notes}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>

                    {/* Items List */}
                    <Typography variant="subtitle1" fontWeight={600}>
                      Items Dispensed ({selectedOrder.items.length})
                    </Typography>
                    <TableContainer component={Paper} sx={{ borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedOrder.items.map((item) => (
                            <TableRow key={item.id} hover>
                              <TableCell>
                                <Typography variant="body2">{item.product_name || 'Unknown Product'}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={item.quantity}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                            <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              <Chip
                                label={selectedOrder.items.reduce((sum, item) => sum + item.quantity, 0)}
                                size="small"
                                color="primary"
                              />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                ) : (
                  <Alert severity="error">Failed to load order details</Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseModal}>Close</Button>
              </DialogActions>
            </Dialog>

            {/* Refresh Indicator */}
            {refreshing && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 80,
                  right: 24,
                  zIndex: 1100
                }}
              >
                <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="caption">Refreshing...</Typography>
                  </Stack>
                </Paper>
              </Box>
            )}

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

          </Box>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default DispenseHistoryFb;